<script>
import { mapGetters } from 'vuex';
import { get } from '@shell/utils/object';
import { mapPref, GROUP_RESOURCES } from '@shell/store/prefs';
import ButtonGroup from '@shell/components/ButtonGroup';
import SortableTable from '@shell/components/SortableTable';
import { NAMESPACE, AGE } from '@shell/config/table-headers';
import { COUNT } from '@shell/config/types';
import { findBy } from '@shell/utils/array';
import { ExtensionPoint, TableColumnLocation, TableLocation } from '@shell/core/types';
import { getApplicableExtensionEnhancements } from '@shell/core/plugin-helpers';
import { ToggleSwitch } from '@components/Form/ToggleSwitch';
import { dateText, fieldValue, monthLabel, stringifyValue } from '@shell/utils/table-views/fields';
import ResourceTableViews, { TABLE_GROUPING_PREFIX } from '@shell/mixins/resource-table-views';
import ResourceTableWatch from '@shell/mixins/resource-table-watch';
import paginationUtils from '@shell/utils/pagination-utils';
import TableViewControls from '@shell/components/TableViews/TableViewControls.vue';
import TableViewTabs from '@shell/components/TableViews/TableViewTabs.vue';
import { runTableViewShortcut } from '@shell/utils/table-views/shortcuts';

// Default group-by in the case the group stored in the preference does not apply
const DEFAULT_GROUP = 'namespace';

// A paginated list's namespace grouping - see STEVE_LIST_GROUPS
const PAGINATED_NAMESPACE_GROUP = 'metadata.namespace';

export const defaultTableSortGenerationFn = (schema, $store) => {
  if ( !schema ) {
    return null;
  }

  const resource = schema.id;
  let sortKey = resource;

  const inStore = $store.getters['currentStore'](resource);
  const generation = $store.getters[`${ inStore }/currentGeneration`]?.(resource);

  if ( generation ) {
    sortKey += `/${ generation }`;
  }

  const nsFilterKey = $store.getters['activeNamespaceCacheKey'];

  if ( nsFilterKey ) {
    return `${ sortKey }/${ nsFilterKey }`;
  }

  // covers case where we have no current cluster's ns cache
  return sortKey;
};

export default {

  name: 'ResourceTable',

  emits: ['clickedActionButton', 'group-change'],

  components: {
    ButtonGroup, SortableTable, TableViewControls, TableViewTabs, ToggleSwitch
  },

  mixins: [
    ResourceTableWatch,
    ResourceTableViews
  ],

  props: {
    schema: {
      type:    Object,
      default: null,
    },

    rows: {
      type:     Array,
      required: true
    },

    loading: {
      type:     Boolean,
      required: false
    },

    altLoading: {
      type:     Boolean,
      required: false
    },

    keyField: {
      // Field that is unique for each row.
      type:    String,
      default: '_key',
    },

    headers: {
      type:    Array,
      default: null,
    },

    namespaced: {
      type:    Boolean,
      default: null, // Automatic from schema
    },

    search: {
      // Show search input to filter rows
      type:    Boolean,
      default: true
    },

    tableActions: {
      // Show bulk table actions
      type:    [Boolean, null],
      default: null
    },

    pagingLabel: {
      type:    String,
      default: 'sortableTable.paging.resource',
    },

    /**
     * Additional params to pass to the pagingLabel translation
     */
    pagingParams: {
      type:    Object,
      default: null,
    },

    rowActions: {
      type:    Boolean,
      default: true,
    },

    /**
     * Field to group rows by, row[groupBy] must be something that can be a map key
     */
    groupBy: {
      type:    String,
      default: null
    },

    /** Declared so a caller's value doesn't override the toolbar's - see `viewGroupSort` */
    groupSort: {
      type:    String,
      default: null
    },

    /**
     * Override any product based group options
     */
    groupOptions: {
      type:    Array,
      default: null
    },

    /**
     * Labels the page's own groupings, as SortableTable's `groupRef` does
     */
    groupRef: {
      type:    [String, Function],
      default: null
    },

    groupable: {
      type:    Boolean,
      default: null, // Null: auto based on namespaced and type custom groupings
    },

    /**
     * If the current preference for group isn't applicable, or not set, use this instead (else the
     * namespace). Naming one, even the namespace, also starts the table's views grouped by it
     */
    groupDefault: {
      type:    String,
      default: null,
    },

    groupTooltip: {
      type:    String,
      default: 'resourceTable.groupBy.namespace',
    },

    overflowX: {
      type:    Boolean,
      default: false
    },
    overflowY: {
      type:    Boolean,
      default: false
    },
    sortGenerationFn: {
      type:    Function,
      default: null,
    },
    getCustomDetailLink: {
      type:    Function,
      default: null
    },
    ignoreFilter: {
      type:    Boolean,
      default: false
    },
    hasAdvancedFiltering: {
      type:    Boolean,
      default: false
    },
    advFilterHideLabelsAsCols: {
      type:    Boolean,
      default: false
    },
    advFilterPreventFilteringLabels: {
      type:    Boolean,
      default: false
    },
    /**
     * Allows for the usage of a query param to work for simple filtering (q)
     */
    useQueryParamsForSimpleFiltering: {
      type:    Boolean,
      default: false
    },
    /**
     * Manual force the update of live and delayed cells. Change this number to kick off the update
     */
    forceUpdateLiveAndDelayed: {
      type:    Number,
      default: 0
    },

    externalPaginationEnabled: {
      type:    Boolean,
      default: false
    },

    externalPaginationResult: {
      type:    Object,
      default: null
    },

    rowsPerPage: {
      type:    Number,
      default: null, // Default comes from the user preference
    },

    overrideInStore: {
      type:    String,
      default: undefined,
    },

    /** The owning list's pagination args, to export every matching row rather than the page */
    externalPaginationArgs: {
      type:    Object,
      default: null
    },

    /** The namespace/project selection and the page's own filters, without the query */
    externalPaginationScope: {
      type:    Object,
      default: null
    },


  },

  data() {
    // Confirm which store we're in, if schema isn't available we're probably showing a list with different types
    const inStore = this.overrideInStore || (this.schema?.id ? this.$store.getters['currentStore'](this.schema.id) : undefined);

    return {
      inStore,

      /**
       * Override the sortGenerationFn given changes in the rows we pass through to sortable table
       *
       * Primary purpose is to directly connect an iteration of `rows` with a sortGeneration string. This avoids
       * reactivity issues where `rows` hasn't yet changed but something like workspaces has (stale values stored against fresh key)
       */
      sortGeneration: undefined,

      listAutoRefreshToggleEnabled: paginationUtils.listAutoRefreshToggleEnabled({ rootGetters: this.$store.getters }),

      hasSearchFilter: false,
    };
  },

  watch: {
    filteredRows: {
      handler() {
        // This is only prevalent in fleet world and the workspace switcher
        // - it's singular (a --> b --> c) instead of namespace switchers additive (a --> a+b --> a)
        // - this means it's much more likely to switch between resource sets containing the same mount of rows
        //
        if (this.currentProduct.showWorkspaceSwitcher) {
          this.sortGeneration = this.safeSortGenerationFn(this.schema, this.$store);
        }
      },
      immediate: true
    },

    // For a page that draws itself differently while grouped, eg projects and namespaces
    group: {
      handler(neu) {
        this.$emit('group-change', neu);
      },
      immediate: true
    },
  },

  computed: {
    options() {
      return this.$store.getters[`type-map/optionsFor`](this.schema, this.externalPaginationEnabled);
    },

    _listGroupMapped() {
      return this.options?.listGroups?.reduce((acc, grp) => {
        acc[grp.value] = grp;

        return acc;
      }, {});
    },

    _mandatorySort() {
      return this.options?.listMandatorySort;
    },

    ...mapGetters(['currentProduct']),

    isNamespaced() {
      if ( this.namespaced !== null ) {
        return this.namespaced;
      }

      return !!get( this.schema, 'attributes.namespaced');
    },

    showNamespaceColumn() {
      // With the toolbar, grouping never takes a column away: the View menu decides the columns
      const groupNamespaces = this.group === 'namespace' && !this.showTableViews;
      const out = !this.showGrouping || !groupNamespaces;

      return out;
    },

    _showBulkActions() {
      if (this.tableActions !== null) {
        return this.tableActions;
      } else if (this.schema) {
        const hideTableActions = this.$store.getters['type-map/hideBulkActionsFor'](this.schema);

        return !hideTableActions;
      }

      return false;
    },

    _headers() {
      // :TableColumn[]
      let headers;
      const showNamespace = this.showNamespaceColumn;

      if ( this.headers ) {
        headers = this.headers.slice();
      } else {
        headers = this.$store.getters['type-map/headersFor'](this.schema, this.externalPaginationEnabled);
      }

      // add custom table columns provided by the extensions ExtensionPoint.TABLE_COL hook
      // gate it so that we prevent errors on older versions of dashboard
      if (this.$store.$extension?.getUIConfig) {
        // { column: TableColumn, paginationColumn: PaginationTableColumn }[]
        const extensionCols = getApplicableExtensionEnhancements(this, ExtensionPoint.TABLE_COL, TableColumnLocation.RESOURCE, this.$route);

        // adding extension defined cols to the correct header config
        extensionCols.forEach((config) => {
          let { column: col, paginationColumn } = config;

          if (this.externalPaginationEnabled) {
            if (paginationColumn) {
              // Use the pagination column, no need to
              col = paginationColumn;
            } else {
              // Attempt to fall back on the single column

              // validate that the required settings are supplied to enable search and sort server-side
              // these do not check other invalid scenarios like a path is a string but to a model property, or that the field supports sort/search via api (some basic non-breaking checks are done further on)
              if (
                col.search !== false && // search is explicitly disabled
                (typeof col.search !== 'string' && !Array.isArray(col.search)) && // primary property path to search on
                typeof col.value !== 'string' // secondary property path to search on
              ) {
                console.warn(`Unable to support server-side search for extension provided column "${ col.name || col.label || col.labelKey }" (column must provide \`search\` or \`value\` property containing a path to a property in the resource. search can be an array).`); // eslint-disable-line no-console

                col.search = false;
              }

              if (
                col.sort !== false && // sort is explicitly disabled
                (typeof col.sort !== 'string' && !Array.isArray(col.sort)) // primary property path to sort on
              ) {
                console.warn(`Unable to support server-side sort for extension provided column "${ col.name || col.label || col.labelKey }" (column must provide \`sort\` property containing a path to a property, or array of paths, in the resource)`); // eslint-disable-line no-console

                col.sort = false;
              }
            }
          }

          // we need the 'value' prop to be populated in order for the rows to show the values
          if (!col.value && col.getValue) {
            col.value = col.getValue;
          }

          // Establish a valid header position for the new table column
          let insertPosition = headers.length;

          if (headers.length > 0) {
            const ageColIndex = headers.findIndex((h) => h.name === AGE.name);

            if (ageColIndex >= 0) {
              // we will allow for the table col to be added right after the AGE col
              // but that will be the limit
              insertPosition = ageColIndex + 1;
            } else {
              // we've found some labels with ' ', which isn't necessarily empty (explore action/button)
              // if we are to add cols, let's push them before these so that the UI doesn't look weird
              const lastViableColIndex = headers.findIndex((h) => (!h.label || !h.label?.trim()) && (!h.labelKey || !h.labelKey?.trim()));

              if (lastViableColIndex >= 0) {
                insertPosition = lastViableColIndex;
              }
            }
          }

          // apply table col ordering if it's present on the new table col config
          if (col.weight) {
            if (col.weight < 0) {
              insertPosition = 0;
            } else if (col.weight < insertPosition) {
              insertPosition = col.weight;
            }
          }

          headers.splice(insertPosition, 0, col);
        });
      }

      // If only one namespace is selected, hide the namespace column
      if ( !showNamespace ) {
        const idx = headers.findIndex((header) => header.name === NAMESPACE.name);

        if ( idx >= 0 ) {
          headers.splice(idx, 1);
        }
      }

      // If we are grouping by a custom group, it may specify that we hide a specific column
      const custom = this._listGroupMapped?.[this.group];

      let hideColumn;

      if (custom?.hideColumn) {
        hideColumn = custom.hideColumn;
      } else {
        const componentCustom = this.groupOptions?.find((go) => go.value === this.group);

        hideColumn = componentCustom?.hideColumn;
      }

      if (hideColumn && !this.showTableViews) {
        const idx = headers.findIndex((header) => header.name === hideColumn);

        if ( idx >= 0 ) {
          headers.splice(idx, 1);
        }
      }

      return headers;
    },

    _applicableExtensionTableHooks() {
      if (this.$store.$extension?.getUIConfig) {
        const extensionTableHooks = getApplicableExtensionEnhancements(this, ExtensionPoint.TABLE, TableLocation.RESOURCE, this.$route);

        return extensionTableHooks;
      }

      return [];
    },

    /**
     * Take rows and filter out entries given the namespace filter
     */
    filteredRows() {
      const isAll = this.$store.getters['isAllNamespaces'];

      // Do we need to filter by namespace like things?
      if (
        !this.isNamespaced || // Resource type isn't namespaced
        this.ignoreFilter || // Component owner strictly states no filtering
        this.externalPaginationEnabled ||
        (isAll && !this.currentProduct?.hideSystemResources) || // Need all
        (this.inStore ? this.$store.getters[`${ this.inStore }/haveNamespace`](this.schema.id)?.length : false)// Store reports type has namespace filter, so rows already contain the correctly filtered resources
      ) {
        return this.rows || [];
      }

      const includedNamespaces = this.$store.getters['namespaces']();

      // Shouldn't happen, but does for resources like management.cattle.io.preference
      if (!this.rows) {
        return [];
      }

      const haveAllNamespace = this.$store.getters['haveAllNamespace'];

      return this.rows.filter((row) => {
        if (this.currentProduct?.hideSystemResources && this.isNamespaced) {
          return !!includedNamespaces[row.metadata.namespace] && !row.isSystemResource;
        } else if (!this.isNamespaced) {
          return true;
        } else if (haveAllNamespace) {
          // `rows` only contains resource from a single namespace
          return true;
        } else {
          return !!includedNamespaces[row.metadata.namespace];
        }
      });
    },

    _group: mapPref(GROUP_RESOURCES),

    // The group stored in the preference (above) might not be valid for this resource table - so ensure we
    // choose a group that is applicable (the default)
    // This saves us from having to store a group preference per resource type - given that custom groupings aer not used much
    // and it feels like a good UX to be able to keep the namespace/flat grouping across tables
    group: {
      get() {
        // The toolbar says how the table is grouped, and a column grouping is none of these
        if (this.showTableViews) {
          return this.viewTableGrouping?.value || 'none';
        }

        // Check group is valid
        const exists = this._groupOptions.find((g) => g.value === this._group);

        if (!exists) {
          // Attempt to find the default option in available options...
          // if not use the first value in the options collection...
          // and if not that just fall back to the default
          const groupDefault = this.groupDefault || DEFAULT_GROUP;

          if (this._groupOptions.find((g) => g.value === groupDefault)) {
            return groupDefault;
          }

          return this._groupOptions[0]?.value || groupDefault;
        }

        return this._group;
      },
      set(value) {
        this._group = value;
      }
    },

    showGrouping() {
      if ( this.groupable === null ) {
        const namespaceGroupable = this.$store.getters['isMultipleNamespaces'] && this.isNamespaced;
        const customGroupable = !!this.options?.listGroups?.length;

        return namespaceGroupable || customGroupable || this.groupOptions?.length;
      }

      return this.groupable || false;
    },

    computedGroupBy() {
      // A toolbar group can be any field, including a label, so the key is a function
      if (this.viewGroupField) {
        const field = this.viewGroupField;
        const empty = this.t('tableViews.group.empty');

        if (field.byMonth) {
          return (row) => {
            const date = dateText(fieldValue(row, field));

            return /^\d{4}-\d{2}/.test(date) ? date.slice(0, 7) : empty;
          };
        }

        return (row) => stringifyValue(fieldValue(row, field)) || empty;
      }

      // If we're not showing grouping options we shouldn't have a group by property
      if (!this.showGrouping) {
        return null;
      }

      if ( this.groupBy ) {
        // This probably comes from the type-map config for the resource (see ResourceList)
        return this.groupBy;
      }

      if ( this.group === 'namespace' ) {
        // This switches to group rows by a key which is the label for the group (??)
        return 'groupByLabel';
      }

      const custom = this._listGroupMapped?.[this.group];

      if (custom?.field) {
        // Override the normal filtering
        return custom.field;
      }

      const componentCustom = this.groupOptions?.find((go) => go.value === this.group);

      if (componentCustom?.field) {
        return componentCustom.field;
      }

      return null;
    },

    /** A toolbar grouping's labels, eg a month by name; the page's own `group-ref` is for its own groupings */
    computedGroupRef() {
      const field = this.viewGroupField;
      const groupBy = this.computedGroupBy;

      if (!field || typeof groupBy !== 'function') {
        return this.groupRef;
      }

      return field.byMonth ? (row) => monthLabel(groupBy(row)) : groupBy;
    },

    /**
     * The groupings this list brings beyond grouping by a column, offered in the toolbar's Group By.
     * None while the list says it can't be grouped, as the old buttons were hidden then. The plain
     * namespace grouping stands in for the namespace column, as a paginated list's does: it groups by
     * the resource's own namespace, which some lists' column doesn't show
     */
    tableGroupings() {
      if (!this.showGrouping) {
        return [];
      }

      return this._groupOptions
        .filter((option) => option.value !== 'none')
        .map((option) => (option.value === DEFAULT_GROUP && !option.field && !option.hideColumn ? { ...option, hideColumn: NAMESPACE.name } : option));
    },

    /**
     * The grouping a view starts with: the one the page names as its default, eg cluster members by
     * role, machines by pool, namespaces by project; else none, whenever the list offers to group by
     * namespace, as a paginated list does under its own name; else the first it offers, eg app bundles
     * by type, secrets by project. A list grouped by a field it is given, eg project members by
     * project, keeps that
     */
    defaultGroupBy() {
      if (!this.showGrouping) {
        return null;
      }

      const offered = (value) => this.tableGroupings.find((option) => option.value === value);
      const offersNamespace = offered(DEFAULT_GROUP) || offered(PAGINATED_NAMESPACE_GROUP);
      const start = offered(this.groupDefault) || (offersNamespace ? null : this.tableGroupings[0]);

      if (!start || (this.groupBy && !start.field)) {
        return null;
      }

      return `${ TABLE_GROUPING_PREFIX }${ start.value }`;
    },

    _groupOptions() {
      if (this.groupOptions) {
        return this.groupOptions;
      }

      // Ignore the defaults below, we have an override set of groups
      // REPLACE (instead of SUPPLEMENT) defaults with listGroups (given listGroupsWillOverride is true)
      if (this.options?.listGroupsWillOverride && !!this.options?.listGroups?.length) {
        return this.options?.listGroups;
      }

      const standard = [
        {
          tooltipKey: 'resourceTable.groupBy.none',
          icon:       'icon-list-flat',
          value:      'none',
        }
      ];

      if (!this.options?.hiddenNamespaceGroupButton) {
        standard.push( {
          tooltipKey: this.groupTooltip,
          icon:       'icon-folder',
          value:      'namespace',
        });
      }

      // SUPPLEMENT (instead of REPLACE) defaults with listGroups (given listGroupsWillOverride is false)
      if (!!this.options?.listGroups?.length) {
        return standard.concat(this.options.listGroups);
      }

      return standard;
    },

    parsedPagingParams() {
      if (this.pagingParams) {
        return this.pagingParams;
      }

      if ( !this.schema ) {
        return {
          singularLabel: '',
          pluralLabel:   ''
        };
      }

      return {
        singularLabel: this.$store.getters['type-map/labelFor'](this.schema),
        pluralLabel:   this.$store.getters['type-map/labelFor'](this.schema, 99),
      };
    },

    /**
     * Readable, plural name of the resource being listed, used by the table to
     * describe the select all checkbox
     */
    selectAllLabel() {
      return this.parsedPagingParams.pluralLabel;
    },

    /**
     * Get the counts data by namespace for the current resource type
     */
    namespaceCounts() {
      if (!this.inStore || !this.schema?.id) {
        return {};
      }

      const counts = this.$store.getters[`${ this.inStore }/all`](COUNT)?.[0]?.counts || {};

      return counts[this.schema.id]?.namespaces || {};
    },

    /**
     * Whether we should show namespace counts in group tabs
     */
    showNamespaceCounts() {
      return (this.group === 'namespace' || this.group === 'metadata.namespace') && this.isNamespaced && !this.hasSearchFilter && !this.viewGroupField;
    },
  },

  methods: {
    /** For whichever list holds the focus, which needn't be this one - see runTableViewShortcut */
    /** Takes the key only for a table the focus is in, so elsewhere it stays the browser's */
    tableViewShortcut(action, event) {
      if (runTableViewShortcut(action)) {
        event.preventDefault();
      }
    },

    keyAction(action) {
      const table = this.$refs.table;

      if ( !table ) {
        return;
      }

      const selection = table.selectedRows;

      if ( action === 'remove' ) {
        const act = findBy(table.availableActions, 'action', 'promptRemove');

        if ( act ) {
          table.setBulkActionOfInterest(act);
          table.applyTableAction(act);
        }

        return;
      }

      if ( selection.length !== 1 ) {
        return;
      }

      switch ( action ) {
      case 'detail':
        selection[0].goToDetail();
        break;
      case 'edit':
        selection[0].goToEdit();
        break;
      case 'yaml':
        selection[0].goToViewYaml();
        break;
      }
    },

    clearSelection() {
      this.$refs.table.clearSelection();
    },

    safeSortGenerationFn() {
      if (this.sortGenerationFn) {
        return this.sortGenerationFn(this.schema, this.$store);
      }

      return defaultTableSortGenerationFn(this.schema, this.$store);
    },

    handleActionButtonClick(event) {
      this.$emit('clickedActionButton', event);
    },

    handleEnterKeyPress(event) {
      if (event.key === 'Enter') {
        this.keyAction('detail');
      }
    },

    // this is where we handle the callbacks to the TABLE extension hooks
    handleSortableTableInteraction(arg) {
      if (this._applicableExtensionTableHooks?.length) {
        this._applicableExtensionTableHooks.forEach((item) => {
          if (item.tableHook) {
            item.tableHook(arg);
          }
        });
      }

      this.hasSearchFilter = !!arg?.filtering?.searchQuery;
      this.recordSort(arg?.sorting);
    },
  }
};
</script>

<template>
  <SortableTable
    ref="table"
    v-bind="$attrs"
    :headers="viewHeaders"
    :rows="viewRows"
    :loading="loading || viewSwitching"
    :alt-loading="altLoading && !viewSwitching"
    :group-by="computedGroupBy"
    :group-ref="computedGroupRef"
    :group-sort="viewGroupSort"
    :group="group"
    :group-options="_groupOptions"
    :search="showTableViews ? false : search"
    :table-views-layout="showTableViews"
    :paging="true"
    :paging-params="parsedPagingParams"
    :paging-label="pagingLabel"
    :select-all-label="selectAllLabel"
    :rows-per-page="rowsPerPage"
    :row-actions="rowActions"
    :table-actions="_showBulkActions"
    :overflow-x="overflowX"
    :overflow-y="overflowY"
    :get-custom-detail-link="getCustomDetailLink"
    :has-advanced-filtering="hasAdvancedFiltering"
    :adv-filter-hide-labels-as-cols="advFilterHideLabelsAsCols"
    :adv-filter-prevent-filtering-labels="advFilterPreventFilteringLabels"
    :key-field="keyField"
    :sortGeneration="sortGeneration"
    :sort-generation-fn="safeSortGenerationFn"
    :use-query-params-for-simple-filtering="useQueryParamsForSimpleFiltering"
    :force-update-live-and-delayed="forceUpdateLiveAndDelayed"
    :external-pagination-enabled="externalPaginationEnabled"
    :external-pagination-result="externalPaginationResult"
    :view-filters="appliedViewFilters"
    :queried="showTableViews && viewTerms.length > 0"
    :view-query="showTableViews ? settledQuery : null"
    :aria-busy="viewBusy ? 'true' : undefined"
    :mandatory-sort="_mandatorySort"
    @clickedActionButton="handleActionButtonClick"
    @group-value-change="group = $event"
    @enter="handleEnterKeyPress"
    @sortable-table-interaction="handleSortableTableInteraction"
  >
    <template
      v-if="showTableViewTabs"
      #table-views
    >
      <TableViewTabs
        :view="view"
        :view-counts="tabCounts"
        :match-count="viewMatchCount"
        :resource-type="schema ? schema.id : ''"
        :table-views-page="tableViewsPage"
        :initial-view-id="openedViewId"
        @update:view="openTabView"
        @tab-queries="tabQueries = $event"
        @export="handleExport"
      />
    </template>

    <template
      v-if="showGrouping && _groupOptions.length > 1 && !showTableViews"
      #header-middle
    >
      <slot name="more-header-middle" />

      <ButtonGroup
        v-model:value="group"
        :options="_groupOptions"
        size="medium"
      />
    </template>

    <template
      v-if="showGrouping || showTableViews"
      #header-right
    >
      <TableViewControls
        v-if="showTableViews"
        :view="view"
        :fields="viewQueryFields"
        :group-fields="viewGroupFields"
        :default-group-by="defaultGroupBy"
        :filter-fields="viewFilterFields"
        :date-fields="viewDateFieldIds"
        :field-values="viewFieldValues"
        :pending-fields="viewPendingFields"
        :loading-values="fieldValuesLoading"
        :rows="filteredRows"
        :unsupported-fields="unsupportedViewFields"
        :default-columns="defaultColumnIds"
        :core-columns="coreColumnIds"
        :history-key="tabViewChanges"
        @update:view="view = $event"
        @request-values="fetchFieldValues"
        @refresh-values="refreshFieldValues"
      />
      <slot
        name="header-right"
      />
    </template>

    <template
      v-if="externalPaginationEnabled"
      #watch-controls
    >
      <!-- See https://github.com/rancher/dashboard/issues/14359 -->
      <ToggleSwitch
        v-if="listAutoRefreshToggleEnabled"
        class="auto-update"
        :value="watching"
        name="label-system-toggle"
        :on-label="t('resourceTable.autoRefresh.label')"
        @update:value="toggleWatch"
      />
    </template>

    <template #group-by="{group: thisGroup}">
      <div class="group-tab">
        <span v-clean-html="thisGroup.ref" />
        <span
          v-if="showNamespaceCounts && Number.isInteger(namespaceCounts[thisGroup.rows?.[0]?.metadata?.namespace]?.count)"
          class="count"
        >
          ({{ namespaceCounts[thisGroup.rows?.[0]?.metadata?.namespace]?.count }})
        </span>
      </div>
    </template>

    <!-- Pass down templates provided by the caller, except `header-right`, which this fills and
         renders the caller's inside - Vue keeps the last template given for a slot name -->
    <template
      v-for="(_, slot) of passthroughSlots"
      :key="slot"
      v-slot:[slot]="scope"
    >
      <slot
        :name="slot"
        v-bind="scope"
      />
    </template>

    <template #shortkeys>
      <!-- The saved view shortcuts. Not `once`: macOS sends no keyup for a key pressed with Cmd held.
           `anywhere`, as the query box has the focus while a view is being edited. `claim`, so with the
           focus outside the table the browser still saves the page (Cmd+S) or bookmarks it (Cmd+D) -->
      <template v-if="showTableViewTabs">
        <button
          v-shortkey.anywhere.claim="{windows: ['ctrl', 's'], mac: ['meta', 's']}"
          class="hide"
          @shortkey="tableViewShortcut('saveChanges', $event)"
        />
        <button
          v-shortkey.anywhere.claim="{windows: ['ctrl', 'shift', 's'], mac: ['meta', 'shift', 's']}"
          class="hide"
          @shortkey="tableViewShortcut('openSaveAsNew', $event)"
        />
        <button
          v-shortkey.anywhere.claim="{windows: ['ctrl', 'd'], mac: ['meta', 'd']}"
          class="hide"
          @shortkey="tableViewShortcut('duplicateCurrent', $event)"
        />
      </template>
      <!-- These act on the selection, so they need the table's actions -->
      <button
        v-if="_showBulkActions"
        v-shortkey.once="['e']"
        class="hide"
        @shortkey="keyAction('edit')"
      />
      <button
        v-if="_showBulkActions"
        v-shortkey.once="['y']"
        class="hide"
        @shortkey="keyAction('yaml')"
      />
      <button
        v-if="_showBulkActions"
        v-shortkey.once="['del']"
        class="hide"
        @shortkey="keyAction('remove')"
      />
      <button
        v-if="_showBulkActions"
        v-shortkey.once="['backspace']"
        class="hide"
        @shortkey="keyAction('remove')"
      />
    </template>
  </SortableTable>
</template>

<style lang="scss" scoped>
.auto-update {
  min-width: 150px; height: 40px
}

.group-tab .count {
  opacity: 0.7;
  margin-left: 2px;
}
</style>
